// Fairness — position-innings heatmap (validated green sequential ramp) and
// the Game Balls leaderboard. Free tier. Data logic ported from the old
// Field Time tab; visuals restyled to GS.

import React from 'react';
import { MapPin } from 'lucide-react';
import { GS } from '../../lib/designTokens';
import { formatInningsOuts } from '../../lib/gc-innings';
import { Card, JerseyChip, shortName } from './ui';
import type { SeasonData } from './useSeasonStats';

const POSITIONS = ['P', 'C', '1B', '2B', 'SS', '3B', 'LF', 'CF', 'RF'] as const;
const INFIELD = new Set(['P', 'C', '1B', '2B', 'SS', '3B']);

// Validated sequential green ramp (dataviz: monotone L, visible step gaps;
// near-zero recedes toward the surface — every cell carries its number).
const RAMP = ['#DCF5EA', '#A5E6CC', '#57D3A6', '#10B981', '#077A54'];

const cellStyle = (innings: number, pos: string, maxInnings: number): React.CSSProperties => {
  if (innings === 0) {
    // Gap: position never played. Infield gaps get a visible ring — more notable.
    return INFIELD.has(pos)
      ? { background: GS.panel2, color: GS.ink2, boxShadow: `inset 0 0 0 1px ${GS.line}` }
      : { background: GS.panel2, color: GS.ink3 };
  }
  const intensity = innings / maxInnings;
  const idx = intensity >= 0.8 ? 4 : intensity >= 0.55 ? 3 : intensity >= 0.35 ? 2 : intensity >= 0.15 ? 1 : 0;
  return { background: RAMP[idx], color: idx >= 3 ? '#FFFFFF' : GS.ink, fontWeight: 800 };
};

export const FairnessSection = ({ data, onOpenReport }: {
  data: SeasonData;
  /** Opens a player's printable Field Time Report. Omitted in Season History,
   *  where buildFieldTimeReport would return an empty record: it filters to the
   *  team's CURRENT season, so a past season's games are excluded. */
  onOpenReport?: (playerId: string) => void;
}) => {
  const statsList = Object.values(data.stats).sort((a: any, b: any) =>
    a.player.name.localeCompare(b.player.name)
  );

  const maxInnings = Math.max(
    1,
    ...statsList.flatMap((s: any) => POSITIONS.map(pos =>
      pos === 'CF' ? s.positions.CF + s.positions.LCF + s.positions.RCF : s.positions[pos] || 0
    ))
  );

  if (data.totalGamesWithLogs === 0) {
    return (
      <Card className="p-6 text-center">
        <MapPin className="w-8 h-8 mx-auto" style={{ color: GS.ink3 }} />
        <p className="text-sm font-black mt-2" style={{ color: GS.ink }}>No innings logged yet</p>
        <p className="text-[11px] font-semibold mt-1" style={{ color: GS.ink2 }}>Complete a game to see position history.</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto px-2 py-3">
        <table className="mx-auto" style={{ borderCollapse: 'separate', borderSpacing: 3 }} aria-label="Innings by player and position">
          <thead>
            <tr>
              <th />
              {POSITIONS.map(pos => (
                <th key={pos} className="text-[8px] font-extrabold uppercase px-0.5 pb-0.5" style={{ color: GS.ink3 }}>{pos}</th>
              ))}
              <th className="text-[8px] font-extrabold uppercase px-0.5 pb-0.5" style={{ color: GS.ink3 }}>Bn</th>
            </tr>
          </thead>
          <tbody>
            {statsList.map((s: any) => {
              const cfTotal = s.positions.CF + s.positions.LCF + s.positions.RCF;
              return (
                <tr key={s.player.id}>
                  <th className="text-right pr-2 text-[10px] font-extrabold whitespace-nowrap" style={{ color: GS.ink }}>
                    {onOpenReport ? (
                      // The name is the handle: you are already looking at this
                      // kid's innings when a parent asks about them, so the
                      // printable record belongs one tap from the row, not buried
                      // behind an unlabelled icon in the player-edit form.
                      // Reads as a link, not as label text. A dotted grey
                      // underline under 10px bold type rendered to literally
                      // nothing (the regenerated screenshot was byte-identical
                      // to the one predating this affordance), and an
                      // undiscoverable control defeats the point of moving the
                      // report here in the first place.
                      <button
                        onClick={() => onOpenReport(s.player.id)}
                        className="underline underline-offset-2 decoration-1"
                        style={{ color: GS.greenInk, textDecorationColor: GS.greenInk }}
                        title={`Field Time Report for ${s.player.name}`}
                      >
                        {shortName(s.player.name)}
                      </button>
                    ) : shortName(s.player.name)}
                  </th>
                  {POSITIONS.map(pos => {
                    // Values accumulate as true fractions (GC writes thirds).
                    // Show the SAME notation GameChanger does ("4.1" is four
                    // innings and one out): rounding to whole innings made a
                    // coach comparing the two screens trust GC's numbers over
                    // Dugout's. The raw value drives the color ramp so shading
                    // stays continuous rather than stepping on rounded buckets.
                    const raw = pos === 'CF' ? cfTotal : (s.positions[pos] || 0);
                    return (
                      <td
                        key={pos}
                        className="text-center text-[9px] rounded-md tabular-nums"
                        style={{ width: 38, height: 26, ...cellStyle(raw, pos, maxInnings) }}
                      >
                        {raw > 0 ? formatInningsOuts(raw) : '—'}
                      </td>
                    );
                  })}
                  <td className="text-center text-[9px] rounded-md font-bold tabular-nums" style={{ width: 38, height: 26, background: GS.panel2, color: s.bench > 0 ? GS.ink2 : GS.ink3 }}>
                    {s.bench > 0 ? formatInningsOuts(s.bench) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-center gap-1.5 pb-2">
        <span className="text-[8px] font-extrabold uppercase" style={{ color: GS.ink3, letterSpacing: '0.07em' }}>Fewer</span>
        {RAMP.map(c => <span key={c} className="w-4 h-2 rounded" style={{ background: c }} />)}
        <span className="text-[8px] font-extrabold uppercase" style={{ color: GS.ink3, letterSpacing: '0.07em' }}>More innings</span>
      </div>
      {onOpenReport && (
        <p className="text-center text-[9px] font-semibold pb-3 px-4 leading-relaxed" style={{ color: GS.ink3 }}>
          Tap a name for their Field Time Report, a one-page record of where they played you can hand a parent.
        </p>
      )}
    </Card>
  );
};

export const GameBallsCard = ({ team, players, viewSeason }: {
  team: any; players: any[]; viewSeason?: { season: string; year: number } | null;
}) => {
  if (!(team.settings?.trackGameBalls ?? false)) return null;
  const isHistoryMode = !!viewSeason;
  const seasonGBKey = viewSeason ? `${viewSeason.year}-${viewSeason.season}` : null;

  const gbList = players
    .map((p: any) => ({
      player: p,
      count: isHistoryMode
        ? (p.seasonStats?.[seasonGBKey!]?.seasonGameBalls ?? 0)
        : (p.seasonGameBalls ?? 0),
    }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.player.name.localeCompare(b.player.name);
    });

  const totalAwarded = gbList.reduce((sum, g) => sum + g.count, 0);
  if (totalAwarded === 0) return null;

  return (
    <Card>
      <div className="flex justify-between px-4 pt-3.5 pb-1 text-[10px] font-extrabold" style={{ color: GS.ink }}>
        <span>Game Balls</span>
        <span style={{ color: GS.ink3 }}>{totalAwarded} awarded</span>
      </div>
      {gbList.filter(g => g.count > 0).map((g, i) => (
        <div key={g.player.id} className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: i > 0 ? `1px solid ${GS.line}` : undefined }}>
          <JerseyChip number={g.player.number} />
          <span className="flex-1 text-xs font-extrabold truncate" style={{ color: GS.ink }}>{shortName(g.player.name)}</span>
          <span className="text-sm" aria-hidden="true">{'⚾'.repeat(Math.min(g.count, 5))}</span>
          <span className="w-5 text-right text-sm font-black tabular-nums" style={{ color: GS.greenInk }}>{g.count}</span>
        </div>
      ))}
    </Card>
  );
};
